import { useState } from "react";
import "./Reports.css";

const REPORT_TYPES = [
  { value: "incident", label: "Incident Report", icon: "⚠️" },
  { value: "patrol", label: "Patrol Summary", icon: "🚔" },
  { value: "equipment", label: "Equipment Issue", icon: "🔧" },
  { value: "suspect", label: "Suspect / Person Report", icon: "🧑" },
];

const SEVERITY = ["Low", "Medium", "High", "Critical"];

const initialForm = {
  reportType: "",
  title: "",
  location: "",
  date: new Date().toISOString().slice(0, 10),
  time: new Date().toTimeString().slice(0, 5),
  severity: "Medium",
  description: "",
  actionTaken: "",
  witnessName: "",
  witnessContact: "",
};

export default function Reports() {
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [recentReports] = useState([
    {
      id: "RPT-2026-0041",
      type: "Incident Report",
      title: "Road accident near MG Road",
      date: "22 May 2026",
      severity: "High",
      status: "Submitted",
    },
    {
      id: "RPT-2026-0040",
      type: "Patrol Summary",
      title: "Evening patrol - Benz Circle",
      date: "21 May 2026",
      severity: "Low",
      status: "Reviewed",
    },
    {
      id: "RPT-2026-0039",
      type: "Equipment Issue",
      title: "Baton battery low alert",
      date: "21 May 2026",
      severity: "Medium",
      status: "Pending",
    },
  ]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3500);
    setForm(initialForm);
  };

  const severityClass = (s) =>
    ({ Low: "sev-low", Medium: "sev-med", High: "sev-high", Critical: "sev-crit" }[s] || "");

  const statusClass = (s) =>
    ({ Submitted: "stat-submitted", Reviewed: "stat-reviewed", Pending: "stat-pending" }[s] || "");

  return (
    <div className="reports-page">
      {submitted && (
        <div className="toast-success">
          <span className="toast-icon">✅</span>
          Report submitted successfully!
        </div>
      )}

      <div className="reports-header">
        <div>
          <h1 className="reports-title">Reports</h1>
          <p className="reports-sub">Submit field reports to the command centre</p>
        </div>
        <div className="rpt-badge">
          <span className="live-dot" /> LIVE SYNC
        </div>
      </div>

      <div className="reports-grid">
        {/* ── LEFT: Form ── */}
        <div className="report-form-card">
          <div className="card-heading">
            <span className="card-icon">📋</span>
            <span>New Report</span>
          </div>

          <form onSubmit={handleSubmit} className="rpt-form">
            {/* Report Type */}
            <div className="field-group">
              <label className="field-label">Report Type *</label>
              <div className="type-grid">
                {REPORT_TYPES.map((rt) => (
                  <button
                    type="button"
                    key={rt.value}
                    className={`type-btn ${form.reportType === rt.value ? "type-btn--active" : ""}`}
                    onClick={() => setForm({ ...form, reportType: rt.value })}
                  >
                    <span className="type-icon">{rt.icon}</span>
                    <span>{rt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div className="field-group">
              <label className="field-label">Report Title *</label>
              <input
                className="rpt-input"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="Brief title describing the event"
                required
              />
            </div>

            {/* Location + Date + Time */}
            <div className="field-row-3">
              <div className="field-group">
                <label className="field-label">Location *</label>
                <input
                  className="rpt-input"
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  placeholder="e.g. Benz Circle"
                  required
                />
              </div>
              <div className="field-group">
                <label className="field-label">Date</label>
                <input
                  className="rpt-input"
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                />
              </div>
              <div className="field-group">
                <label className="field-label">Time</label>
                <input
                  className="rpt-input"
                  type="time"
                  name="time"
                  value={form.time}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Severity */}
            <div className="field-group">
              <label className="field-label">Severity Level</label>
              <div className="sev-row">
                {SEVERITY.map((s) => (
                  <button
                    type="button"
                    key={s}
                    className={`sev-btn ${severityClass(s)} ${form.severity === s ? "sev-btn--active" : ""}`}
                    onClick={() => setForm({ ...form, severity: s })}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="field-group">
              <label className="field-label">Incident Description *</label>
              <textarea
                className="rpt-input rpt-textarea"
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Describe what happened in detail..."
                rows={4}
                required
              />
            </div>

            {/* Action Taken */}
            <div className="field-group">
              <label className="field-label">Action Taken</label>
              <textarea
                className="rpt-input rpt-textarea"
                name="actionTaken"
                value={form.actionTaken}
                onChange={handleChange}
                placeholder="What steps did you take on the field?"
                rows={3}
              />
            </div>

            {/* Witness */}
            <div className="field-row-2">
              <div className="field-group">
                <label className="field-label">Witness Name</label>
                <input
                  className="rpt-input"
                  name="witnessName"
                  value={form.witnessName}
                  onChange={handleChange}
                  placeholder="Optional"
                />
              </div>
              <div className="field-group">
                <label className="field-label">Witness Contact</label>
                <input
                  className="rpt-input"
                  name="witnessContact"
                  value={form.witnessContact}
                  onChange={handleChange}
                  placeholder="Phone / ID"
                />
              </div>
            </div>

            <button type="submit" className="submit-btn">
              <span>📤</span> Submit Report
            </button>
          </form>
        </div>

        {/* ── RIGHT: Recent Reports ── */}
        <div className="recent-card">
          <div className="card-heading">
            <span className="card-icon">🕐</span>
            <span>Recent Reports</span>
          </div>
          <div className="recent-list">
            {recentReports.map((r) => (
              <div key={r.id} className="recent-item">
                <div className="recent-top">
                  <span className="recent-id">{r.id}</span>
                  <span className={`status-tag ${statusClass(r.status)}`}>{r.status}</span>
                </div>
                <div className="recent-title">{r.title}</div>
                <div className="recent-meta">
                  <span className="recent-type">{r.type}</span>
                  <span className={`sev-pill ${severityClass(r.severity)}`}>{r.severity}</span>
                  <span className="recent-date">{r.date}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Quick stats */}
          <div className="stats-row">
            <div className="stat-box">
              <div className="stat-num">41</div>
              <div className="stat-lbl">Total Reports</div>
            </div>
            <div className="stat-box">
              <div className="stat-num">3</div>
              <div className="stat-lbl">Pending Review</div>
            </div>
            <div className="stat-box">
              <div className="stat-num">38</div>
              <div className="stat-lbl">Reviewed</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
